import type {
  AssessmentStatus,
  ChatMessage,
  FinOpsInsightPanel,
} from "@/types";
import { mockInsightPanelSnapshot } from "./insights-panel";

export type MockAssessmentDetail = {
  id: string;
  title: string;
  status: AssessmentStatus;
  attachments: { id: string; name: string; type: string }[];
};

const defaultId = "sess_demo_001";

const assessments: Record<string, MockAssessmentDetail> = {
  sess_demo_001: {
    id: "sess_demo_001",
    title: "Q1 Cloud spend review",
    status: "active",
    attachments: [
      { id: "f1", name: "cost_usage_mar2026.csv", type: "CSV" },
      { id: "f2", name: "architecture-data-platform.pdf", type: "PDF" },
      { id: "f3", name: "budget_variance.xlsx", type: "Sheet" },
    ],
  },
  sess_demo_002: {
    id: "sess_demo_002",
    title: "Kubernetes rightsizing",
    status: "draft",
    attachments: [{ id: "f1", name: "kube-metrics-export.json", type: "JSON" }],
  },
  sess_demo_003: {
    id: "sess_demo_003",
    title: "Reserved instances audit",
    status: "completed",
    attachments: [
      { id: "f1", name: "ri_inventory.csv", type: "CSV" },
      { id: "f2", name: "slo_definitions.md", type: "MD" },
    ],
  },
};

const messagesBySession: Record<string, ChatMessage[]> = {
  sess_demo_001: [
    {
      id: "m-1",
      role: "assistant",
      content:
        "Scope confirmed for Q1 cloud spend. Below is the current read of your upload and stated goals.",
      createdAt: "2026-04-06T10:00:00.000Z",
      structured: {
        summary:
          "Compute and data transfer dominate the increase; non-prod clusters show sustained low utilization in two families.",
        findings: [
          {
            category: "Compute Rightsizing",
            estimatedAmount: 1200,
            description: "Scale down underutilized m5 families in dev/staging to t3/t4g types."
          },
          {
            category: "Storage Optimization",
            estimatedAmount: 450,
            description: "Clean up unattached EBS volumes and migrate old snapshots to Cold HDD storage."
          },
          {
            category: "Network Egress Review",
            estimatedAmount: 300,
            description: "Align nightly batch windows to avoid peak egress pricing windows."
          }
        ],
        estimatedSavings: "$1,800 - $2,500 monthly potential",
        nextQuestion: "",
      },
    },
    {
      id: "m-2",
      role: "user",
      content:
        "EC2 growth is outpacing revenue—where should we dig in first for leadership?",
      createdAt: "2026-04-06T10:00:40.000Z",
    },
    {
      id: "m-3",
      role: "assistant",
      content:
        "For leadership, anchor on three layers: workload mix, commitment posture, and unit economics vs. SLOs. I can generate a one-page brief once you confirm finance’s savings recognition rules.",
      createdAt: "2026-04-06T10:01:05.000Z",
    },
  ],
  sess_demo_002: [
    {
      id: "k-1",
      role: "assistant",
      content:
        "Draft session — attach additional utilization series to tighten rightsizing recommendations.",
      createdAt: "2026-04-05T09:00:00.000Z",
    },
    {
      id: "k-2",
      role: "user",
      content: "We only have node-level CPU so far.",
      createdAt: "2026-04-05T09:02:00.000Z",
    },
  ],
  sess_demo_003: [
    {
      id: "r-1",
      role: "assistant",
      content:
        "Assessment marked complete. Export the final report from the header when your stakeholders are ready.",
      createdAt: "2026-03-28T16:00:00.000Z",
    },
  ],
};

const insightVariants: Record<string, FinOpsInsightPanel> = {
  sess_demo_001: mockInsightPanelSnapshot,
  sess_demo_002: {
    ...mockInsightPanelSnapshot,
    estimatedMonthlySavings: {
      amountLabel: "$54,200",
      subtitle: "Preliminary — needs utilization depth on EKS.",
      breakdown: [
        { label: "Node rightsizing (est.)", value: "$31,000" },
        { label: "Spot / mixed instances", value: "$14,200" },
        { label: "Storage class tuning", value: "$9,000" },
      ],
    },
    riskProfile: {
      level: "moderate",
      summary:
        "Data is incomplete for prod-adjacent clusters; recommendations carry wider bands.",
      signals: ["Limited metrics retention", "Draft session scope"],
    },
    confidenceLevel: {
      percent: 54,
      label: "Medium",
      rationale:
        "Raise confidence by attaching pod-level utilization and autoscaling events.",
    },
  },
  sess_demo_003: {
    ...mockInsightPanelSnapshot,
    estimatedMonthlySavings: {
      amountLabel: "$89,100",
      subtitle: "Audit complete — modeled against on-demand baseline.",
      breakdown: [
        { label: "RI / SP alignment", value: "$52,400" },
        { label: "Instance family consolidation", value: "$22,700" },
        { label: "Waste instances", value: "$14,000" },
      ],
    },
    riskProfile: {
      level: "low",
      summary: "Commitment coverage improved; residual risk is contract timing.",
      signals: ["Completed inventory", "Stakeholder sign-off recorded"],
    },
    confidenceLevel: {
      percent: 86,
      label: "High",
      rationale: "Full billing export and RI inventory attached for this session.",
    },
  },
};

export function getMockAssessmentDetail(sessionId: string): MockAssessmentDetail {
  const known = assessments[sessionId];
  if (known) return known;
  return {
    id: sessionId,
    title: "Ad hoc assessment",
    status: "active",
    attachments: [
      { id: "u1", name: "cost_export_placeholder.csv", type: "CSV" },
    ],
  };
}

export function getMockChatMessages(sessionId: string): ChatMessage[] {
  return messagesBySession[sessionId] ?? messagesBySession[defaultId];
}

export function getMockInsightPanel(sessionId: string): FinOpsInsightPanel {
  return insightVariants[sessionId] ?? mockInsightPanelSnapshot;
}
